/**
 * Admin-only voice stylization (content editor save + preview).
 * Processing runs in the admin app when saving; learners always play pre-uploaded
 * server files (audioUrl / sticky playback URL) with no client-side effects chain.
 */

import { normalizeAppHostedUploadUrl } from './normalizeAppHostedUploadUrl';
import { pitchShiftAudioBufferPreserveDuration } from './pitchPreserveShift';

/** Resolve hosted audio paths for admin fetch/preview (recordings, sticky clips, originals). */
export function resolveAudioMediaUrl(url: string | null | undefined): string {
  if (url == null) return '';
  const raw = String(url).trim();
  if (!raw || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
  if (/^https?:\/\//i.test(raw) || raw.startsWith('/uploads/')) {
    return normalizeAppHostedUploadUrl(raw);
  }
  if (!raw.startsWith('/')) {
    return normalizeAppHostedUploadUrl(`/uploads/audio/${raw}`);
  }
  return normalizeAppHostedUploadUrl(raw);
}

export type VoiceTheme = 'original' | 'man' | 'woman' | 'child' | 'bot' | 'fun' | 'custom';

/** Tunable processing parameters (presets + custom fine-tune). */
export interface VoiceThemeSettings {
  pitchSemitones: number;
  chorusMix: number;
  gain: number;
  bassDb: number;
  trebleDb: number;
  presenceHz?: number;
  presenceDb?: number;
  bandpassHz?: number;
  bandpassQ?: number;
  distortion?: number;
  reverbMix?: number;
}

export interface VoiceThemePreset extends VoiceThemeSettings {
  id: VoiceTheme;
  label: string;
  description: string;
}

export const DEFAULT_VOICE_SETTINGS: VoiceThemeSettings = {
  pitchSemitones: 0,
  chorusMix: 0,
  gain: 1,
  bassDb: 0,
  trebleDb: 0
};

export function presetToSettings(preset: VoiceThemePreset): VoiceThemeSettings {
  return {
    pitchSemitones: preset.pitchSemitones,
    chorusMix: preset.chorusMix,
    gain: preset.gain,
    bassDb: preset.bassDb,
    trebleDb: preset.trebleDb,
    ...(preset.presenceHz !== undefined ? { presenceHz: preset.presenceHz } : {}),
    ...(preset.presenceDb !== undefined ? { presenceDb: preset.presenceDb } : {}),
    ...(preset.bandpassHz !== undefined ? { bandpassHz: preset.bandpassHz } : {}),
    ...(preset.bandpassQ !== undefined ? { bandpassQ: preset.bandpassQ } : {}),
    ...(preset.distortion !== undefined ? { distortion: preset.distortion } : {}),
    ...(preset.reverbMix !== undefined ? { reverbMix: preset.reverbMix } : {})
  };
}

export function getVoiceThemeLabel(theme: VoiceTheme, settings?: VoiceThemeSettings): string {
  if (theme === 'custom') {
    return `Custom (${settings?.pitchSemitones ?? 0} st)`;
  }
  return getVoiceThemePreset(theme).label;
}

export const VOICE_THEME_PRESETS: VoiceThemePreset[] = [
  {
    id: 'original',
    label: 'Original',
    description: 'Unchanged recording',
    pitchSemitones: 0,
    chorusMix: 0,
    gain: 1,
    bassDb: 0,
    trebleDb: 0
  },
  {
    id: 'man',
    label: 'Man',
    description: 'Lower pitch, fuller body — same speed',
    pitchSemitones: -4.5,
    chorusMix: 0.14,
    gain: 1.1,
    bassDb: 7,
    trebleDb: -5,
    presenceHz: 520,
    presenceDb: 4
  },
  {
    id: 'woman',
    label: 'Woman',
    description: 'Higher, lighter — same speed',
    pitchSemitones: 3.5,
    chorusMix: 0.16,
    gain: 1.06,
    bassDb: -4,
    trebleDb: 6,
    presenceHz: 2800,
    presenceDb: 5
  },
  {
    id: 'child',
    label: 'Child',
    description: 'Bright young voice — same speed',
    pitchSemitones: 5.5,
    chorusMix: 0.18,
    gain: 1.08,
    bassDb: -5,
    trebleDb: 8,
    presenceHz: 3200,
    presenceDb: 6
  },
  {
    id: 'bot',
    label: 'Bot',
    description: 'Robotic filter on disguised base',
    pitchSemitones: -1.5,
    chorusMix: 0.08,
    gain: 1.12,
    bassDb: -2,
    trebleDb: 4,
    bandpassHz: 1100,
    bandpassQ: 3.2,
    distortion: 0.52
  },
  {
    id: 'fun',
    label: 'Fun',
    description: 'Cartoonish character — same speed',
    pitchSemitones: 6.5,
    chorusMix: 0.12,
    gain: 1.14,
    bassDb: 4,
    trebleDb: 9,
    reverbMix: 0.26
  }
];

export function getVoiceThemePreset(theme: VoiceTheme): VoiceThemePreset {
  return VOICE_THEME_PRESETS.find((p) => p.id === theme) ?? VOICE_THEME_PRESETS[0]!;
}

function makeDistortionCurve(amount: number): Float32Array<ArrayBuffer> {
  const samples = 44100;
  const curve = new Float32Array(samples);
  const k = amount * 100;
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((3 + k) * x * 20 * (Math.PI / 180)) / (Math.PI + k * Math.abs(x));
  }
  return curve;
}

function createImpulseBuffer(
  ctx: OfflineAudioContext,
  durationSec: number,
  decay: number
): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(rate * durationSec));
  const impulse = ctx.createBuffer(2, length, rate);
  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  return impulse;
}

function connectThemeChain(
  ctx: OfflineAudioContext,
  source: AudioBufferSourceNode,
  settings: VoiceThemeSettings
): AudioNode {
  const gain = ctx.createGain();
  gain.gain.value = settings.gain;
  source.connect(gain);

  let node: AudioNode = gain;

  if (settings.bassDb !== 0) {
    const bass = ctx.createBiquadFilter();
    bass.type = 'lowshelf';
    bass.frequency.value = 220;
    bass.gain.value = settings.bassDb;
    node.connect(bass);
    node = bass;
  }

  if (settings.presenceHz && settings.presenceDb) {
    const presence = ctx.createBiquadFilter();
    presence.type = 'peaking';
    presence.frequency.value = settings.presenceHz;
    presence.Q.value = 1.2;
    presence.gain.value = settings.presenceDb;
    node.connect(presence);
    node = presence;
  }

  if (settings.trebleDb !== 0) {
    const treble = ctx.createBiquadFilter();
    treble.type = 'highshelf';
    treble.frequency.value = 3200;
    treble.gain.value = settings.trebleDb;
    node.connect(treble);
    node = treble;
  }

  if (settings.bandpassHz) {
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = settings.bandpassHz;
    band.Q.value = settings.bandpassQ ?? 2;
    node.connect(band);
    node = band;
  }

  if (settings.distortion && settings.distortion > 0) {
    const shaper = ctx.createWaveShaper();
    shaper.curve = makeDistortionCurve(settings.distortion);
    shaper.oversample = '4x';
    node.connect(shaper);
    node = shaper;
  }

  if (settings.reverbMix && settings.reverbMix > 0) {
    const dry = ctx.createGain();
    const wet = ctx.createGain();
    dry.gain.value = 1 - settings.reverbMix;
    wet.gain.value = settings.reverbMix;

    const convolver = ctx.createConvolver();
    convolver.buffer = createImpulseBuffer(ctx, 0.6, 2.5);

    node.connect(dry);
    node.connect(convolver);
    convolver.connect(wet);

    const merge = ctx.createGain();
    merge.gain.value = 1;
    dry.connect(merge);
    wet.connect(merge);
    return merge;
  }

  return node;
}

/** Encode AudioBuffer as 16-bit PCM WAV blob. */
export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const dataLength = buffer.length * blockAlign;
  const arrayBuffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) {
    channels.push(buffer.getChannelData(c));
  }

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numChannels; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c]![i]!));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

export async function applyVoiceTheme(
  input: Blob,
  theme: VoiceTheme,
  settings: VoiceThemeSettings = DEFAULT_VOICE_SETTINGS
): Promise<Blob> {
  if (theme === 'original') {
    return input;
  }

  const arrayBuffer = await input.arrayBuffer();

  const decodeCtx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    await decodeCtx.close();
  }

  const pitched = await pitchShiftAudioBufferPreserveDuration(
    decoded,
    settings.pitchSemitones,
    settings.chorusMix
  );

  const offline = new OfflineAudioContext(
    pitched.numberOfChannels,
    pitched.length,
    pitched.sampleRate
  );

  const source = offline.createBufferSource();
  source.buffer = pitched;
  source.playbackRate.value = 1;

  const outputNode = connectThemeChain(offline, source, settings);
  outputNode.connect(offline.destination);

  source.start(0);
  const rendered = await offline.startRendering();
  return audioBufferToWavBlob(rendered);
}

export async function resolvePendingBlockAudioBlob(options: {
  audioUrl?: string;
  getRecordedBlob?: () => Blob | undefined;
  getUploadedFile?: () => File | undefined;
}): Promise<Blob | null> {
  const recorded = options.getRecordedBlob?.();
  if (recorded) {
    return recorded;
  }

  const file = options.getUploadedFile?.();
  if (file) {
    return new Blob([file], { type: file.type || 'application/octet-stream' });
  }

  return resolveBlockAudioBlob({
    ...(options.audioUrl !== undefined ? { audioUrl: options.audioUrl } : {})
  });
}

export async function resolveBlockAudioBlob(options: {
  audioUrl?: string;
  getRecordedBlob?: () => Blob | undefined;
  getUploadedFile?: () => File | undefined;
}): Promise<Blob | null> {
  const recorded = options.getRecordedBlob?.();
  if (recorded) {
    return recorded;
  }

  const file = options.getUploadedFile?.();
  if (file) {
    return new Blob([file], { type: file.type || 'application/octet-stream' });
  }

  const url = options.audioUrl?.trim();
  if (!url) {
    return null;
  }

  if (url.startsWith('blob:') || url.startsWith('http') || url.startsWith('/') || url.startsWith('data:')) {
    try {
      const fetchUrl =
        url.startsWith('blob:') || url.startsWith('data:') ? url : normalizeAppHostedUploadUrl(url);
      const response = await fetch(fetchUrl);
      if (!response.ok) {
        return null;
      }
      return await response.blob();
    } catch {
      return null;
    }
  }

  return null;
}

export async function applyVoiceStyleSettings(
  input: Blob,
  settings: VoiceThemeSettings
): Promise<Blob> {
  return applyVoiceTheme(input, 'custom', settings);
}

const serverAudioBlobCache = new Map<string, Promise<Blob>>();

export async function fetchAudioBlobFromUrl(url: string): Promise<Blob> {
  const resolved =
    url.startsWith('blob:') || url.startsWith('data:') ? url : resolveAudioMediaUrl(url);

  if (resolved.startsWith('blob:') || resolved.startsWith('data:')) {
    const response = await fetch(resolved);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio file (${response.status})`);
    }
    return response.blob();
  }

  let pending = serverAudioBlobCache.get(resolved);
  if (!pending) {
    pending = fetch(resolved).then((response) => {
      if (!response.ok) {
        serverAudioBlobCache.delete(resolved);
        throw new Error(`Failed to fetch audio file (${response.status})`);
      }
      return response.blob();
    });
    serverAudioBlobCache.set(resolved, pending);
  }
  return pending;
}

/** Resolve the unprocessed source blob for voice stylization. */
export async function resolveOriginalBlockAudioBlob(options: {
  originalAudioUrl?: string;
  previewAudioUrl?: string;
  getRecordedBlob?: () => Blob | undefined;
  getUploadedFile?: () => File | undefined;
}): Promise<Blob | null> {
  const recorded = options.getRecordedBlob?.();
  if (recorded) return recorded;

  const file = options.getUploadedFile?.();
  if (file) {
    return new Blob([file], { type: file.type || 'application/octet-stream' });
  }

  const pendingUrl = options.previewAudioUrl;
  if (pendingUrl?.startsWith('blob:') && !options.originalAudioUrl) {
    return fetchAudioBlobFromUrl(pendingUrl);
  }

  const originalUrl = options.originalAudioUrl;
  if (originalUrl?.startsWith('blob:')) {
    return fetchAudioBlobFromUrl(originalUrl);
  }
  if (originalUrl) {
    return fetchAudioBlobFromUrl(originalUrl);
  }

  const previewUrl = options.previewAudioUrl;
  if (previewUrl?.startsWith('blob:')) {
    return fetchAudioBlobFromUrl(previewUrl);
  }
  if (previewUrl && !previewUrl.startsWith('data:')) {
    return fetchAudioBlobFromUrl(previewUrl);
  }

  return null;
}

export async function prepareAudioBlobForUpload(
  rawBlob: Blob,
  theme: VoiceTheme,
  settings: VoiceThemeSettings = DEFAULT_VOICE_SETTINGS
): Promise<Blob> {
  return applyVoiceTheme(rawBlob, theme, settings);
}

/** Play styled preview from original source. Returns cleanup stop function. */
export async function playVoiceStylePreview(
  input: Blob,
  settings: VoiceThemeSettings,
  onEnded?: () => void
): Promise<() => void> {
  return playVoiceThemePreview(input, 'custom', settings, onEnded);
}

/** Play themed preview without persisting. Returns cleanup stop function. */
export async function playVoiceThemePreview(
  input: Blob,
  theme: VoiceTheme,
  settings: VoiceThemeSettings = DEFAULT_VOICE_SETTINGS,
  onEnded?: () => void
): Promise<() => void> {
  const processed = await applyVoiceTheme(input, theme, settings);
  const url = URL.createObjectURL(processed);
  const audio = new Audio(url);

  const cleanup = () => {
    audio.pause();
    URL.revokeObjectURL(url);
    onEnded?.();
  };

  audio.onended = cleanup;
  audio.onerror = cleanup;

  try {
    await audio.play();
  } catch {
    cleanup();
    throw new Error('Could not play preview. Check browser audio permissions.');
  }

  return cleanup;
}
